import { useEffect } from 'react';
import 'firebase/auth';
import { useRootStoreContext } from '../stores/RootStore';
import { parseISO } from 'date-fns';
import {
    HubEventTypes,
    HubType,
    MessageModel,
    MessageModelAPIResponse,
} from '../types/SignalR';
import {
    HttpTransportType,
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from '@microsoft/signalr';

const hubConnections: Record<HubType, HubConnection | null> = {
    paymentEventHub: null,
    // Insert more hubs here...
};

// TODO: Extend this hook to handle subscription to multiple hubs and multiple events within the hubs
const useSocket = <T extends HubType, K extends HubEventTypes[T]>(
    hub: HubType,
    event: K,
    onReceive: (data: MessageModel) => void,
    queryParams: string | undefined = '',
) => {
    const {
        SocketStore: {
            reducer: [, socketStoreDispatch],
        },
    } = useRootStoreContext();

    useEffect(() => {
        if (
            hubConnections[hub] &&
            hubConnections[hub]?.state !== HubConnectionState.Disconnected
        ) {
            // Connection to hub already exists
            return;
        }

        const updateConnectionState = (connectionState: HubConnectionState) =>
            socketStoreDispatch({
                type: 'ConnectionStates',
                payload: { hub, connectionState },
            });

        const openConnection = () => {
            const conn = new HubConnectionBuilder()
                .withUrl(
                    `${import.meta.env.VITE_API_ENDPOINT}/${hub}${queryParams}`,
                    {
                        transport: HttpTransportType.LongPolling, // TODO: change to websocket
                    },
                )
                .withAutomaticReconnect([
                    1000, 1000, 1000, 3000, 3000, 3000, 3000, 3000, 3000, 3000,
                    3000, 3000, 3000, 3000, 5000, 5000, 5000, 5000, 5000, 5000,
                    5000, 5000, 10000, 20000, 30000,
                ])
                .configureLogging(LogLevel.Information)
                .build();

            hubConnections[hub] = conn;
            return conn;
        };

        const registerConnectionEvents = (connection: HubConnection) => {
            connection.onclose(() => {
                updateConnectionState(HubConnectionState.Disconnected);
            });

            connection.onreconnected(() => {
                updateConnectionState(HubConnectionState.Connected);
            });

            connection.onreconnecting(() => {
                updateConnectionState(HubConnectionState.Reconnecting);
            });
        };

        const createHubConnection = async (connection: HubConnection) => {
            try {
                updateConnectionState(HubConnectionState.Connecting);

                await connection
                    .start()
                    .then(() => {
                        updateConnectionState(HubConnectionState.Connected);
                        console.log(`Connection successful to hub ${hub} :)`);
                    })
                    .catch(() => {
                        updateConnectionState(HubConnectionState.Disconnected);
                        console.log(`Could not connect to hub ${hub} :(`);
                    });
            } catch (err) {
                updateConnectionState(HubConnectionState.Disconnected);
                console.log(`Could not connect to hub ${hub} :(`);
            }
        };

        const conn = openConnection();
        registerConnectionEvents(conn);
        createHubConnection(conn);
    }, [hub, socketStoreDispatch, queryParams]);

    useEffect(() => {
        const connection = hubConnections[hub];

        if (connection) {
            const handler = (data: MessageModelAPIResponse) =>
                onReceive({ ...data, sentDate: parseISO(data.sentDate) });
            connection.on(event as string, handler);

            return () => {
                connection.off(event, handler);
            };
        }
    }, [onReceive, hub, event]);

    useEffect(() => {
        return () => {
            const connection = hubConnections[hub];
            if (connection) {
                console.log(`Closing connection to hub: ${hub}`);
                connection.stop();
            }
        };
    }, [hub]);
};

export default useSocket;
